import { useState, useEffect } from 'react';
import { Menu, X, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'Portfolio', href: '#portfolio' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) {
    setMobileOpen(false);
    return;
  }

  const headerHeight = document.querySelector('header')?.offsetHeight || 80;

  setMobileOpen(false);

  setTimeout(() => {
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.pageYOffset - headerHeight + 20,
      behavior: 'smooth',
    });
  }, 500);
};

  return (
    <motion.header
      data-testid="header-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-header py-4' : 'py-6 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a
          href="#hero"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-2 font-heading text-2xl md:text-3xl font-light tracking-tight"
          style={{ color: 'var(--text-primary)' }}
          data-testid="header-logo"
        >
          <Heart size={20} style={{ color: 'var(--primary)' }} className="fill-current" />
          SHInvites
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((link, i) => (
            <motion.button
              key={link.label}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
              onClick={() => scrollTo(link.href)}
              className="link-underline font-body text-sm font-medium uppercase tracking-[0.15em] hover:text-[#D4587A] transition-colors bg-transparent border-none cursor-pointer"
              style={{ color: 'var(--text-secondary)' }}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </motion.button>
          ))}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            onClick={() => scrollTo('#contact')}
            className="btn-primary text-xs rounded-full"
            data-testid="hero-cta-button"
          >
            Book Now
          </motion.button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden bg-transparent border-none cursor-pointer"
          style={{ color: 'var(--text-primary)' }}
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-header overflow-hidden"
          >
            <nav className="flex flex-col items-center gap-6 py-8">
              {navLinks.map((link, i) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => scrollTo(link.href)}
                  className="font-body text-sm font-medium uppercase tracking-[0.15em] hover:text-[#D4587A] transition-colors bg-transparent border-none cursor-pointer"
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </motion.button>
              ))}
              <button
                onClick={() => scrollTo('#contact')}
                className="btn-primary text-xs rounded-full"
                data-testid="mobile-book-now-button"
              >
                Book Now
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
